import { Paragraph } from '@tiptap/extension-paragraph';
import { Code } from '@tiptap/extension-code';
import { Table } from '@tiptap/extension-table';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableRow } from '@tiptap/extension-table-row';
import { HorizontalRule } from '@tiptap/extension-horizontal-rule';
import { Link } from '@tiptap/extension-link';
import { Image } from '@tiptap/extension-image';
import { Extension, Node, mergeAttributes } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';
import useCodeHighlighter from './useCodeHighlighter';
import { cloneData } from './utils';

export const fixAttr = (attributes, attribute, defaultValue) => {
    if (!attributes[attribute]) {
        attributes[attribute] = defaultValue;
    }
    if (attributes[attribute] === null || attributes[attribute] === 'null') {
        attributes[attribute] = defaultValue;
    }
    return attributes[attribute];
};

export const fixCellWidth = (attributes) => {
    if (!attributes?.style.style) {
        attributes.style = { style: '' };
    }
    if (attributes.colwidth) {
        const cl = cloneData(attributes.colwidth);
        const pixels = cl.reduce((accumulator, currentValue) => {
            return accumulator + currentValue;
        }, 0);
        if (parseInt(pixels) > 0) {
            attributes.style = {
                style: `width: ${pixels}px`,
            };
        }
    }
    return attributes.style.style;
};

export const sanitizeHTML = (str) => {
    return str.replace(/(<([^>]+)>)/gi, '');
};

export const CustomImage = Image.extend({
    addAttributes() {
        return {
            class: {
                default: '',
                renderHTML: (attributes) => {
                    return {
                        class: `${attributes.class}`,
                    };
                },
            },
            style: {
                default: '',
                renderHTML: (attributes) => {
                    return {
                        style: `${attributes.style}`,
                    };
                },
            },
            src: {
                default: '',
                parseHTML: (element) => element.getAttribute('src'),
                renderHTML: (attributes) => {
                    return {
                        src: `${attributes.src}`,
                    };
                },
            },
            'data-origin-src': {
                default: '',
                parseHTML: (element) => element.getAttribute('data-origin-src'),
                renderHTML: (attributes) => {
                    return attributes['data-origin-src']
                        ? {
                              'data-origin-src': `${attributes['data-origin-src']}`,
                          }
                        : {};
                },
            },
            'data-editor-width': {
                default: '',
                parseHTML: (element) => element.getAttribute('data-editor-width'),
                renderHTML: (attributes) => {
                    return attributes['data-editor-width']
                        ? {
                              'data-editor-width': `${attributes['data-editor-width']}`,
                          }
                        : {};
                },
            },
            'data-editor-height': {
                default: '',
                parseHTML: (element) => element.getAttribute('data-editor-height'),
                renderHTML: (attributes) => {
                    return attributes['data-editor-height']
                        ? {
                              'data-editor-height': `${attributes['data-editor-height']}`,
                          }
                        : {};
                },
            },
            alt: {
                default: '',
                parseHTML: (element) => element.getAttribute('alt'),
                renderHTML: (attributes) => {
                    return {
                        alt: `${attributes.alt}`,
                    };
                },
            },
        };
    },
    addNodeView() {
        return ({ node, editor, getPos }) => {
            const {
                view,
                options: { editable },
            } = editor;
            const { src, alt, style } = node.attrs;
            const originSrc = node.attrs['data-origin-src'];
            const editorWidth = node.attrs['data-editor-width'];
            const editorHeight = node.attrs['data-editor-height'];
            const $container = document.createElement('div');
            const $img = document.createElement('img');
            $container.appendChild($img);
            $img.setAttribute('src', src);
            $img.setAttribute('alt', alt);
            $img.setAttribute('style', style);
            if (originSrc) {
                $img.setAttribute('data-origin-src', originSrc);
            }
            if (editorWidth) {
                $img.setAttribute('data-editor-width', editorWidth);
            }
            if (editorHeight) {
                $img.setAttribute('data-editor-height', editorHeight);
            }
            $img.setAttribute('draggable', 'true');

            if (!editable) return { dom: $img };
            const dotsPosition = [
                'top: -4px; left: -4px; cursor: nwse-resize;',
                'top: -4px; right: -4px; cursor: nesw-resize;',
                'bottom: -4px; left: -4px; cursor: nesw-resize;',
                'bottom: -4px; right: -4px; cursor: nwse-resize;',
            ];
            let isResizing = false;
            let startX, startWidth, startHeight;
            $container.addEventListener('click', () => {
                if (typeof getPos === 'function') {
                    editor.commands.setNodeSelection(getPos());
                }
                $container.setAttribute(
                    'style',
                    `display: inline-block; position: relative; border: 1px dashed rgb(60, 130, 246); ${style} cursor: pointer;`,
                );
                Array.from({ length: 4 }, (_, index) => {
                    const $dot = document.createElement('div');
                    $dot.setAttribute(
                        'style',
                        `position: absolute; width: 9px; height: 9px; background-color: rgb(60, 130, 246); border: 1.5px solid rgb(60, 130, 246); ${dotsPosition[index]}`,
                    );
                    $dot.addEventListener('mousedown', (e) => {
                        e.preventDefault();
                        isResizing = true;
                        startX = e.clientX;
                        startWidth = $img.offsetWidth;
                        startHeight = $img.offsetHeight;
                        const onMouseMove = (e) => {
                            if (!isResizing) return;
                            const deltaX = e.clientX - startX;
                            const aspectRatio = startWidth / startHeight;
                            const newWidth = startWidth + deltaX;
                            const newHeight = newWidth / aspectRatio;
                            $container.style.width = newWidth + 'px';
                            $container.style.height = newHeight + 'px';
                            $img.style.width = newWidth + 'px';
                            $img.style.height = newHeight + 'px';
                        };
                        const onMouseUp = () => {
                            if (isResizing) {
                                isResizing = false;
                            }
                            if (typeof getPos === 'function') {
                                const newAttrs = {
                                    ...node.attrs,
                                    style: $img.style.cssText,
                                };
                                view.dispatch(
                                    view.state.tr.setNodeMarkup(getPos(), null, newAttrs),
                                );
                            }
                            document.removeEventListener('mousemove', onMouseMove);
                            document.removeEventListener('mouseup', onMouseUp);
                        };
                        document.addEventListener('mousemove', onMouseMove);
                        document.addEventListener('mouseup', onMouseUp);
                    });
                    $container.appendChild($dot);
                });
            });
            document.addEventListener('click', (e) => {
                if (!$container.contains(e.target)) {
                    $container.removeAttribute('style');
                    if ($container.childElementCount > 2) {
                        for (let i = 0; i < 4; i++) {
                            $container.removeChild($container.lastChild);
                        }
                    }
                }
            });
            return {
                dom: $container,
            };
        };
    },
});

export const CustomTable = Table.extend({
    /* addAttributes() {
        return {
            ...this.parent?.(),
            style: {
                default: '',
                parseHTML: (element) => {
                    return {
                        style: element.getAttribute('style'),
                    };
                },
                renderHTML: (attributes) => {
                    return {
                        style: fixCellWidth(attributes),
                    };
                },
            },
        };
    }, */
});

export const CustomTableRow = TableRow.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            class: {
                default: '',
                renderHTML: (attributes) => {
                    return {
                        class: fixAttr(attributes, 'class', 'tpl-tr'),
                    };
                },
            },
        };
    },
});

export const CustomTableHeader = TableHeader.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            style: {
                default: '',
                parseHTML: (element) => {
                    return {
                        style: element.getAttribute('style'),
                    };
                },
                renderHTML: (attributes) => {
                    return {
                        style: fixCellWidth(attributes),
                    };
                },
            },
        };
    },
});

export const CustomTableCell = TableCell.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            style: {
                default: '',
                parseHTML: (element) => {
                    return {
                        style: element.getAttribute('style'),
                    };
                },
                renderHTML: (attributes) => {
                    return {
                        style: fixCellWidth(attributes),
                    };
                },
            },
        };
    },
});

export const CustomParagraph = Paragraph.extend({
    addAttributes() {
        return {
            class: {
                default: 'tpl-p',
                renderHTML: (attributes) => {
                    return {
                        class: fixAttr(attributes, 'class', 'tpl-p'),
                    };
                },
            },
            style: {
                default: '',
                renderHTML: (attributes) => {
                    return {
                        style: fixAttr(attributes, 'style', ''),
                    };
                },
            },
        };
    },
});

export const CustomLink = Link.extend({
    addAttributes() {
        return {
            class: {
                default: 'tpl-link',
                renderHTML: (attributes) => {
                    return {
                        class: fixAttr(attributes, 'class', 'tpl-link'),
                    };
                },
            },
            style: {
                default: '',
                renderHTML: (attributes) => {
                    return {
                        style: fixAttr(attributes, 'style', ''),
                    };
                },
            },
            href: {
                default: '',
                renderHTML: (attributes) => {
                    return {
                        href: attributes.href,
                    };
                },
            },
            target: {
                default: '_blank',
                renderHTML: (attributes) => {
                    return {
                        target: attributes.target,
                    };
                },
            },
        };
    },
});

export const CustomLine = HorizontalRule.extend({
    addAttributes() {
        return {
            class: {
                default: '',
                renderHTML: (attributes) => {
                    return {
                        class: fixAttr(attributes, 'class', ''),
                    };
                },
            },
            style: {
                default: '',
                renderHTML: (attributes) => {
                    return {
                        style: fixAttr(attributes, 'style', ''),
                    };
                },
            },
        };
    },
});

export const CustomCode = Code.extend({
    addAttributes() {
        return {
            class: {
                default: '',
                renderHTML: (attributes) => {
                    return {
                        class: fixAttr(attributes, 'class', ''),
                    };
                },
            },
            style: {
                default: '',
                renderHTML: (attributes) => {
                    return {
                        style: fixAttr(attributes, 'style', ''),
                    };
                },
            },
        };
    },
});

export const CodeHighlighter = Extension.create({
    name: 'codeHighlighter',

    addProseMirrorPlugins() {
        return [
            new Plugin({
                state: {
                    init(_, { doc }) {
                        return useCodeHighlighter(doc);
                    },
                    apply(transaction, oldState) {
                        return transaction.docChanged
                            ? useCodeHighlighter(transaction.doc)
                            : oldState;
                    },
                },
                props: {
                    decorations(state) {
                        return this.getState(state);
                    },
                },
            }),
        ];
    },
});

const PAGE_BREAK_STYLE = 'page-break-after: always; break-after: page;';

export const PageBreak = Node.create({
    name: 'pageBreak',
    group: 'block',
    atom: true,
    selectable: true,
    draggable: true,

    addAttributes() {
        return {
            class: {
                default: 'page-break',
                parseHTML: (el) => (el.getAttribute && el.getAttribute('class')) || 'page-break',
                renderHTML: (attrs) => (attrs.class ? { class: attrs.class } : {}),
            },
            style: {
                // držíme inline styl kvůli backend whitelistu
                default: PAGE_BREAK_STYLE,
                parseHTML: (el) => {
                    const style = (el.getAttribute && el.getAttribute('style')) || '';
                    return /page-break-after\s*:\s*always/i.test(style) ? style : PAGE_BREAK_STYLE;
                },
                renderHTML: (attrs) => ({ style: attrs.style || PAGE_BREAK_STYLE }),
            },
        };
    },

    parseHTML() {
        return [
            { tag: 'div.page-break' },
            { tag: 'hr.page-break' },
            { tag: 'p.page-break' },
            {
                tag: 'div',
                getAttrs: (el) => {
                    const style = (el.getAttribute && el.getAttribute('style')) || '';
                    return /page-break-after\s*:\s*always/i.test(style) ? {} : false;
                },
            },
            {
                tag: 'p',
                getAttrs: (el) => {
                    const style = (el.getAttribute && el.getAttribute('style')) || '';
                    return /page-break-after\s*:\s*always/i.test(style) ? {} : false;
                },
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        // vždy renderuj jako <div> s inline stylem
        return [
            'div',
            mergeAttributes({ class: 'page-break', style: PAGE_BREAK_STYLE }, HTMLAttributes),
        ];
    },

    addCommands() {
        return {
            insertPageBreak:
                () =>
                ({ commands }) =>
                    commands.insertContent({
                        type: this.name,
                        attrs: {
                            class: 'page-break',
                            style: PAGE_BREAK_STYLE,
                        },
                    }),
        };
    },
});
export const useWYSIWYGEditorExtensions = () => {
    return {
        fixAttr: fixAttr,
        fixCellWidth: fixCellWidth,
        sanitizeHTML: sanitizeHTML,
        CustomImage: CustomImage,
        CustomLine: CustomLine,
        CustomParagraph: CustomParagraph,
        CustomLink: CustomLink,
        CustomTable: CustomTable,
        CustomTableRow: CustomTableRow,
        CustomTableCell: CustomTableCell,
        CustomTableHeader: CustomTableHeader,
        CustomCode: CustomCode,
        CodeHighlighter: CodeHighlighter,
        PageBreak: PageBreak,
    };
};

export default useWYSIWYGEditorExtensions;
